import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ZoomIn, ZoomOut, RotateCcw, Printer, X, FileText, Maximize2, Minimize2 } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import type { ProcessedArchitectureDrawing, ArchitectureImageUrls } from '@/lib/architecture-drawings';
import { generateSrcSet, getDefaultSizes, getFallbackUrl, getThumbnailUrl, hasValidImages } from '@/lib/architecture-drawings';

/**
 * Floor plan item for the gallery
 */
interface FloorPlanItem {
  id: string;
  label: string;
  thumbnailUrl: string;
  imageUrls?: ArchitectureImageUrls; // For architecture drawings with responsive images
  imageUrl?: string; // For simple uploaded images
  pdfUrl?: string | null;
  isArchitectureDrawing: boolean;
}

interface FloorPlanViewerProps {
  /** Architecture drawing from database (with responsive images) */
  architectureDrawing?: ProcessedArchitectureDrawing | null;
  /** Uploaded floor plan images (simple URLs) */
  uploadedFloorPlans?: string[];
  /** Property/unit name for analytics and alt text */
  propertyName: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Large Image Viewer Component (Internal)
 * Displays a single image with inline zoom/pan controls
 */
interface LargeImageViewerProps {
  item: FloorPlanItem;
  propertyName: string;
  scale: number;
  position: { x: number; y: number };
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onDownloadPng: () => void;
  onDownloadPdf?: () => void;
  onFullscreen?: () => void;
  isFullscreen?: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onWheel: (e: React.WheelEvent) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  showLabel?: boolean;
}

const LargeImageViewer = ({
  item,
  propertyName,
  scale,
  position,
  onZoomIn,
  onZoomOut,
  onReset,
  onDownloadPng,
  onDownloadPdf,
  onFullscreen,
  isFullscreen = false,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onWheel,
  containerRef,
  showLabel = true,
}: LargeImageViewerProps) => {
  return (
    <div className={`relative overflow-hidden ${
      isFullscreen ? 'bg-black rounded-none' : 'bg-muted/20 rounded-lg'
    }`}>
      {/* Floating Controls */}
      <div className="absolute top-4 left-4 z-20 flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onZoomIn}
          className="bg-white/90 hover:bg-white"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onZoomOut}
          className="bg-white/90 hover:bg-white"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onReset}
          className="bg-white/90 hover:bg-white"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
        {onFullscreen && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onFullscreen}
            className="bg-white/90 hover:bg-white"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={onDownloadPng}
          className="bg-white/90 hover:bg-white"
          title="Download PNG"
        >
          <Download className="w-4 h-4" />
        </Button>
        {onDownloadPdf && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownloadPdf}
            className="bg-white/90 hover:bg-white"
            title="Download PDF"
          >
            <FileText className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Label Badge */}
      {showLabel && (
        <div className="absolute top-4 right-4 z-20">
          <Badge variant={item.isArchitectureDrawing ? "default" : "secondary"} className="text-sm px-3 py-1">
            {item.label}
          </Badge>
        </div>
      )}

      {/* Image Container with Zoom/Pan */}
      <div
        ref={containerRef}
        className={`relative overflow-hidden cursor-grab active:cursor-grabbing ${
          isFullscreen ? 'h-screen' : 'h-[500px] lg:h-[600px]'
        }`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
      >
        {item.isArchitectureDrawing && item.imageUrls ? (
          // Responsive image for architecture drawings
          <picture
            className="absolute top-1/2 left-1/2"
            style={{
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
            }}
          >
            <source
              type="image/avif"
              srcSet={generateSrcSet(item.imageUrls, 'avif')}
              sizes={getDefaultSizes()}
            />
            <source
              type="image/webp"
              srcSet={generateSrcSet(item.imageUrls, 'webp')}
              sizes={getDefaultSizes()}
            />
            <img
              src={getFallbackUrl(item.imageUrls)}
              srcSet={generateSrcSet(item.imageUrls, 'jpeg')}
              sizes={getDefaultSizes()}
              alt={`${item.label} for ${propertyName}`}
              className="max-w-none"
              draggable={false}
            />
          </picture>
        ) : (
          // Simple image for uploaded floor plans
          <img
            src={item.imageUrl || item.thumbnailUrl}
            alt={`${item.label} for ${propertyName}`}
            className="absolute top-1/2 left-1/2 max-w-none"
            style={{
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
            }}
            draggable={false}
          />
        )}
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-4 z-20">
        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3 text-white text-xs">
          <p>Scroll to zoom • Drag to pan</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Thumbnail Grid Component (Internal)
 * Displays uploaded floor plans as thumbnails
 */
interface ThumbnailGridProps {
  items: FloorPlanItem[];
  propertyName: string;
  onThumbnailClick: (index: number) => void;
}

const ThumbnailGrid = ({ items, propertyName, onThumbnailClick }: ThumbnailGridProps) => {
  if (items.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">Additional Floor Plans</h3>
        <div className="flex-1 h-px bg-border" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="group relative cursor-pointer"
            onClick={() => onThumbnailClick(index)}
          >
            {/* Thumbnail Image */}
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
              <img
                src={item.thumbnailUrl}
                alt={`${item.label} for ${propertyName}`}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 bg-white"
                loading="lazy"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center rounded-xl">
                <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
            
            {/* Label Badge */}
            <div className="absolute bottom-2 left-2 right-2">
              <Badge variant="secondary" className="text-xs truncate max-w-full">
                {item.label}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FloorPlanViewer = ({ 
  architectureDrawing, 
  uploadedFloorPlans = [], 
  propertyName, 
  className = '' 
}: FloorPlanViewerProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Zoom/pan state for architectural drawing (single mode or top of multiple mode)
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Zoom/pan state for modal (multiple mode only)
  const [modalScale, setModalScale] = useState(1);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  // Build the gallery items array
  const galleryItems: FloorPlanItem[] = [];
  
  // Add architecture drawing first (if available and has valid images)
  if (architectureDrawing && hasValidImages(architectureDrawing)) {
    galleryItems.push({
      id: `arch-${architectureDrawing.unitType}`,
      label: 'Architectural Drawing',
      thumbnailUrl: getThumbnailUrl(architectureDrawing.imageUrls),
      imageUrls: architectureDrawing.imageUrls,
      pdfUrl: architectureDrawing.pdfUrl,
      isArchitectureDrawing: true,
    });
  }
  
  // Add uploaded floor plans
  uploadedFloorPlans.forEach((url, index) => {
    galleryItems.push({
      id: `uploaded-${index}`,
      label: uploadedFloorPlans.length === 1 ? 'Floor Plan' : `Floor Plan ${index + 1}`,
      thumbnailUrl: url,
      imageUrl: url,
      isArchitectureDrawing: false,
    });
  });

  // Determine display mode
  const isSingleImageMode = galleryItems.length === 1 && galleryItems[0]?.isArchitectureDrawing;
  const isMultipleImageMode = galleryItems.length > 1;

  const currentItem = selectedIndex !== null ? galleryItems[selectedIndex] : null;

  useEffect(() => {
    // Track floor plan view
    trackEvent('property_floorplan_view', { property: propertyName });
  }, [propertyName]);

  // Reset zoom/pan when changing images in modal
  useEffect(() => {
    if (selectedIndex !== null) {
      setModalScale(1);
      setModalPosition({ x: 0, y: 0 });
    }
  }, [selectedIndex]);

  const openDetailView = (thumbnailIndex: number) => {
    // In single mode, we don't use modal
    if (isSingleImageMode) return;
    
    // For multiple mode, thumbnailIndex is 0-based from the thumbnail grid
    // galleryItems[0] is architectural drawing (shown at top, no modal)
    // galleryItems[1+] are uploaded plans (shown as thumbnails, open modal)
    const actualIndex = thumbnailIndex + 1; // +1 because galleryItems[0] is architectural
    if (actualIndex < galleryItems.length) {
      setSelectedIndex(actualIndex);
      trackEvent('property_floorplan_detail_open', { 
        property: propertyName, 
        item: galleryItems[actualIndex]?.label 
      });
    }
  };

  const closeDetailView = () => {
    setSelectedIndex(null);
    trackEvent('property_floorplan_detail_close', { property: propertyName });
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 3));
    trackEvent('property_floorplan_zoom', { property: propertyName, action: 'in' });
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.5));
    trackEvent('property_floorplan_zoom', { property: propertyName, action: 'out' });
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    trackEvent('property_floorplan_reset', { property: propertyName });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.5, Math.min(3, prev * delta)));
  };

  // Modal-specific handlers
  const handleModalZoomIn = () => {
    setModalScale(prev => Math.min(prev * 1.2, 3));
    trackEvent('property_floorplan_zoom', { property: propertyName, action: 'in', context: 'modal' });
  };

  const handleModalZoomOut = () => {
    setModalScale(prev => Math.max(prev / 1.2, 0.5));
    trackEvent('property_floorplan_zoom', { property: propertyName, action: 'out', context: 'modal' });
  };

  const handleModalReset = () => {
    setModalScale(1);
    setModalPosition({ x: 0, y: 0 });
    trackEvent('property_floorplan_reset', { property: propertyName, context: 'modal' });
  };

  const handleModalMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - modalPosition.x, y: e.clientY - modalPosition.y });
  };

  const handleModalMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setModalPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleModalWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setModalScale(prev => Math.max(0.5, Math.min(3, prev * delta)));
  };

  // Fullscreen handlers
  const handleFullscreen = async () => {
    const element = fullscreenContainerRef.current;
    if (!element) return;

    try {
      if (!document.fullscreenElement) {
        await element.requestFullscreen();
        setIsFullscreen(true);
        trackEvent('property_floorplan_fullscreen', { property: propertyName, action: 'enter' });
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
        trackEvent('property_floorplan_fullscreen', { property: propertyName, action: 'exit' });
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  // Listen for fullscreen changes (user might exit via ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) {
        trackEvent('property_floorplan_fullscreen', { property: propertyName, action: 'exit' });
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [propertyName]);

  const handleDownloadPng = () => {
    const item = isSingleImageMode ? galleryItems[0] : (currentItem || galleryItems[0]);
    if (!item) return;
    
    const imageUrl = item.imageUrl || 
      (item.imageUrls ? getFallbackUrl(item.imageUrls) : '');
    
    if (imageUrl) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `${propertyName}-${item.label.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.click();
      trackEvent('property_floorplan_download_png', { 
        property: propertyName, 
        item: item.label 
      });
    }
  };

  const handleDownloadPdf = () => {
    const item = isSingleImageMode ? galleryItems[0] : (currentItem || galleryItems[0]);
    if (!item?.pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = item.pdfUrl;
    link.download = `${propertyName}-${item.label.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    link.target = '_blank';
    link.click();
    trackEvent('property_floorplan_download_pdf', { 
      property: propertyName, 
      item: item.label 
    });
  };

  const handlePrint = () => {
    window.print();
    trackEvent('property_floorplan_print', { property: propertyName });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeDetailView();
    if (e.key === 'ArrowLeft' && selectedIndex !== null && selectedIndex > 1) {
      setSelectedIndex(selectedIndex - 1);
    }
    if (e.key === 'ArrowRight' && selectedIndex !== null && selectedIndex < galleryItems.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const navigatePrev = () => {
    if (selectedIndex !== null && selectedIndex > 1) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const navigateNext = () => {
    if (selectedIndex !== null && selectedIndex < galleryItems.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  // If no floor plans available
  if (galleryItems.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-muted-foreground">Floor plan not available for this unit yet.</p>
      </div>
    );
  }

  // Single Image Mode: Only architectural drawing, display full-size with inline controls
  if (isSingleImageMode) {
    return (
      <div className={className} ref={fullscreenContainerRef}>
        <LargeImageViewer
          item={galleryItems[0]}
          propertyName={propertyName}
          scale={scale}
          position={position}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={handleReset}
          onDownloadPng={handleDownloadPng}
          onDownloadPdf={galleryItems[0].pdfUrl ? handleDownloadPdf : undefined}
          onFullscreen={handleFullscreen}
          isFullscreen={isFullscreen}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
          containerRef={containerRef}
          showLabel={true}
        />
      </div>
    );
  }

  // Multiple Images Mode: Architectural drawing at top, uploaded plans as thumbnails below
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Architectural Drawing - Large at Top */}
      {galleryItems[0] && galleryItems[0].isArchitectureDrawing && (
        <div ref={fullscreenContainerRef}>
          <LargeImageViewer
            item={galleryItems[0]}
            propertyName={propertyName}
            scale={scale}
            position={position}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleReset}
            onDownloadPng={handleDownloadPng}
            onDownloadPdf={galleryItems[0].pdfUrl ? handleDownloadPdf : undefined}
            onFullscreen={handleFullscreen}
            isFullscreen={isFullscreen}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
            containerRef={containerRef}
            showLabel={true}
          />
        </div>
      )}

      {/* Uploaded Floor Plans - Thumbnails Below */}
      {galleryItems.length > 1 && (
        <ThumbnailGrid
          items={galleryItems.slice(1)}
          propertyName={propertyName}
          onThumbnailClick={openDetailView}
        />
      )}

      {/* Detail View Modal for Uploaded Plans */}
      {selectedIndex !== null && currentItem && selectedIndex > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeDetailView}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <div 
            className="relative w-full h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Controls Bar */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
              {/* Left Controls - Zoom/Pan */}
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleModalZoomIn}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleModalZoomOut}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleModalReset}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {/* Center - Label */}
              <div className="absolute left-1/2 -translate-x-1/2">
                <Badge variant="secondary" className="text-sm px-4 py-1">
                  {currentItem.label}
                </Badge>
              </div>

              {/* Right Controls - Download/Print/Close */}
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownloadPng}
                  className="bg-white/10 hover:bg-white/20 text-white"
                  title="Download PNG"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrint}
                  className="bg-white/10 hover:bg-white/20 text-white"
                  title="Print"
                >
                  <Printer className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeDetailView}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Image Container with Zoom/Pan */}
            <div
              ref={modalContainerRef}
              className="flex-1 overflow-hidden cursor-grab active:cursor-grabbing"
              onMouseDown={handleModalMouseDown}
              onMouseMove={handleModalMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleModalWheel}
            >
              {currentItem.isArchitectureDrawing && currentItem.imageUrls ? (
                // Responsive image for architecture drawings
                <picture
                  className="absolute top-1/2 left-1/2"
                  style={{
                    transform: `translate(-50%, -50%) translate(${modalPosition.x}px, ${modalPosition.y}px) scale(${modalScale})`,
                  }}
                >
                  <source
                    type="image/avif"
                    srcSet={generateSrcSet(currentItem.imageUrls, 'avif')}
                    sizes={getDefaultSizes()}
                  />
                  <source
                    type="image/webp"
                    srcSet={generateSrcSet(currentItem.imageUrls, 'webp')}
                    sizes={getDefaultSizes()}
                  />
                  <img
                    src={getFallbackUrl(currentItem.imageUrls)}
                    srcSet={generateSrcSet(currentItem.imageUrls, 'jpeg')}
                    sizes={getDefaultSizes()}
                    alt={`${currentItem.label} for ${propertyName}`}
                    className="max-w-none"
                    draggable={false}
                  />
                </picture>
              ) : (
                // Simple image for uploaded floor plans
                <img
                  src={currentItem.imageUrl || currentItem.thumbnailUrl}
                  alt={`${currentItem.label} for ${propertyName}`}
                  className="absolute top-1/2 left-1/2 max-w-none"
                  style={{
                    transform: `translate(-50%, -50%) translate(${modalPosition.x}px, ${modalPosition.y}px) scale(${modalScale})`,
                  }}
                  draggable={false}
                />
              )}
            </div>

            {/* Navigation Arrows */}
            {galleryItems.length > 2 && (
              <>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={navigatePrev}
                  disabled={selectedIndex === 1}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white disabled:opacity-30"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={navigateNext}
                  disabled={selectedIndex === galleryItems.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white disabled:opacity-30"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </>
            )}

            {/* Bottom Info Bar */}
            <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-black/70 to-transparent">
              <div className="flex items-center justify-between">
                {/* Instructions */}
                <div className="text-white/70 text-xs">
                  Scroll to zoom • Drag to pan • Arrow keys to navigate
                </div>
                
                {/* Image Counter */}
                <div className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">
                  {selectedIndex} of {galleryItems.length - 1}
                </div>
              </div>

              {/* Thumbnail Strip */}
              {galleryItems.length > 2 && (
                <div className="flex gap-2 mt-4 justify-center overflow-x-auto pb-2">
                  {galleryItems.slice(1).map((item, index) => {
                    const actualIndex = index + 1;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setSelectedIndex(actualIndex)}
                        className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                          actualIndex === selectedIndex 
                            ? 'border-white' 
                            : 'border-transparent hover:border-white/50'
                        }`}
                      >
                        <img
                          src={item.thumbnailUrl}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-contain bg-white"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
